import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClockIcon } from '@heroicons/react/24/outline';
import type { CurationReviewRow, CurationUndoBatchResult } from '@avisoma/shared';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { Button } from '../../catalyst/button';
import { Badge } from '../../catalyst/badge';
import { Link } from '../../catalyst/link';
import { SortableTable, type SortableTableColumn } from '../../components/SortableTable';
import { InfinityScroll } from '../../components/InfinityScroll';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useInfiniteList } from '../../hooks/useInfiniteList';
import { useSessionStorageState } from '../../hooks/useSessionStorageState';
import { useToast } from '../../components/Toast';
import { formatDateTime } from '../../lib/dateFormat';
import backend from '../../services/backend';
import { CURATION_SOURCE_LABELS } from '../../lib/curationSourceLabels';
import { CurationFilters } from './CurationFilters';
import { canUndoGroup, historyBatchInfo, type HistoryBatchInfo } from './historyBatch';

const PAGE_SIZE = 50;

function UndoCell({
  row,
  group,
  onUndo,
  onUndoGroup,
  onRefresh,
  busyBatchId,
  setBusyBatchId,
}: {
  row: CurationReviewRow;
  group: HistoryBatchInfo | undefined;
  onUndo: (id: number) => void;
  onUndoGroup: (results: CurationUndoBatchResult[]) => void;
  onRefresh: () => void;
  busyBatchId: string | null;
  setBusyBatchId: (batchId: string | null) => void;
}) {
  const batchBusy = !!row.batchId && busyBatchId === row.batchId;
  const undoOne = useMutation({
    mutationFn: () => backend.undoCurationReview(row.id),
    onSuccess: () => onUndo(row.id),
  });
  const undoGroup = useMutation({
    mutationFn: () => backend.undoCurationReviewBatch(row.batchId!),
    onMutate: () => setBusyBatchId(row.batchId),
    onSuccess: (response) => onUndoGroup(response.data.results),
    onError: onRefresh,
    onSettled: () => setBusyBatchId(null),
  });

  if (row.revertedAt && !canUndoGroup(row)) {
    return <Badge color="zinc">Reverted</Badge>;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {row.revertedAt ? (
        <Badge color="zinc">Reverted</Badge>
      ) : (
        <Button outline disabled={undoOne.isPending || batchBusy} onClick={() => undoOne.mutate()}>
          {undoOne.isPending ? 'Undoing…' : 'Undo'}
        </Button>
      )}
      {canUndoGroup(row) && row.batchId && row.id === group?.leadId && (
        <Button outline disabled={undoGroup.isPending} onClick={() => undoGroup.mutate()}>
          {undoGroup.isPending ? 'Undoing…' : 'Undo group'}
        </Button>
      )}
    </div>
  );
}

export default function ReviewHistory() {
  useDocumentTitle('Curation history');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';

  // Persisted in sessionStorage, mirroring the curation list, so the chosen
  // source/category survive a reload or navigating away and back in the tab.
  const [persistedFilters, setPersistedFilters] = useSessionStorageState('curation-history-filters', {
    source: '',
    category: '',
  });
  const sourceFilter = persistedFilters.source;
  const categoryFilter = persistedFilters.category;
  const setSourceFilter = (value: string) => setPersistedFilters((f) => ({ ...f, source: value }));
  const setCategoryFilter = (value: string) => setPersistedFilters((f) => ({ ...f, category: value }));
  const hasActiveFilters = Boolean(sourceFilter || categoryFilter);

  // Memoized so useInfiniteList's resetKey/queryKey keep a stable identity across
  // renders that should NOT reset the window (only an actual filter change resets).
  const filters = useMemo(
    () => ({ source: sourceFilter || undefined, category: categoryFilter || undefined }),
    [sourceFilter, categoryFilter],
  );

  // Key by the viewer's id + effective role: the endpoint returns role-scoped
  // data (admin/supervisor → all curators, otherwise → own), and admins can
  // emulate roles — so the cache must not be shared across identities/roles.
  const queryKey = useMemo(
    () => ['curation-reviews', user?.id, user?.role, filters],
    [user?.id, user?.role, filters],
  );

  const { items, hasMore, loading, loadMore } = useInfiniteList<CurationReviewRow>({
    queryKey,
    queryFn: async (limit) => {
      const rows = (await backend.getCurationReviews({ ...filters, limit })).data.rows;
      // Bumped-limit pagination: a full page implies more may exist. A final exact
      // multiple costs one extra fetch that returns short and clears hasMore.
      return { items: rows, hasMore: rows.length === limit };
    },
    initialItemsPerPage: PAGE_SIZE,
    resetKey: filters,
  });

  const [busyBatchId, setBusyBatchId] = useState<string | null>(null);
  const batches = useMemo(() => historyBatchInfo(items), [items]);

  const refreshAfterUndo = () => {
    queryClient.invalidateQueries({ queryKey: ['curation-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['curation', 'queue'] });
  };

  const handleUndo = (id: number) => {
    toast.success(`Reverted review #${id}`);
    refreshAfterUndo();
  };

  const handleUndoGroup = (results: CurationUndoBatchResult[]) => {
    const undone = results.filter((result) => result.status === 'undone').length;
    const failed = results.filter((result) => result.status === 'failed').length;
    if (failed > 0) {
      toast.error(`Reverted ${undone} reviews. ${failed} did not revert.`);
    } else if (undone === 0) {
      toast.info('No reviews to revert');
    } else {
      toast.success(`Reverted ${undone} reviews in the group`);
    }
    refreshAfterUndo();
  };

  const columns: SortableTableColumn<CurationReviewRow>[] = [
    {
      label: 'Page',
      accessor: 'pageId',
      render: (row) => (
        <Link
          href={`/document-pages/${row.pageId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          View page ↗
        </Link>
      ),
    },
    {
      label: 'Group',
      accessor: 'batchId',
      render: (row) => {
        if (!row.batchId) {
          return <span className="text-sm text-gray-500 dark:text-zinc-400">—</span>;
        }
        return <Badge color="indigo">Group</Badge>;
      },
    },
    {
      label: 'Verdict',
      accessor: 'verdict',
      sortable: true,
      render: (row) => (
        <Badge color={row.verdict === 'corrected' ? 'amber' : 'green'}>{row.verdict}</Badge>
      ),
    },
    {
      label: 'Change',
      accessor: 'newCategory',
      render: (row) =>
        row.verdict === 'corrected' ? (
          <span className="text-sm text-gray-900 dark:text-zinc-100">
            {row.oldCategory ?? '—'} → {row.newCategory ?? '—'}
          </span>
        ) : (
          <span className="text-sm text-gray-500 dark:text-zinc-400">{row.oldCategory ?? '—'}</span>
        ),
    },
    {
      label: 'Source',
      accessor: 'source',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-gray-500 dark:text-zinc-400">
          {CURATION_SOURCE_LABELS[row.source]}
        </span>
      ),
    },
    {
      label: 'When',
      accessor: 'createdAt',
      sortable: true,
      render: (row) => (
        <span className="text-gray-500 dark:text-zinc-400">{formatDateTime(row.createdAt)}</span>
      ),
    },
    {
      label: 'Reviewer',
      accessor: 'reviewerName',
      sortable: true,
      enabled: isAdmin,
      render: (row) => (
        <span className="text-sm text-gray-900 dark:text-zinc-100">{row.reviewerName ?? row.reviewerId}</span>
      ),
    },
    {
      label: 'Undo',
      accessor: 'id',
      render: (row) => (
        <UndoCell
          row={row}
          group={row.batchId ? batches.get(row.batchId) : undefined}
          onUndo={handleUndo}
          onUndoGroup={handleUndoGroup}
          onRefresh={refreshAfterUndo}
          busyBatchId={busyBatchId}
          setBusyBatchId={setBusyBatchId}
        />
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Curation history"
        description={isAdmin ? 'Recent reviews across all curators' : 'Your recent reviews'}
        icon={<ClockIcon className="size-6" />}
      />

      {/* Filter row — shared with the curation list view */}
      <CurationFilters
        source={sourceFilter}
        category={categoryFilter}
        onSourceChange={setSourceFilter}
        onCategoryChange={setCategoryFilter}
      />

      {loading && (
        <div className="space-y-4">
          <Skeleton variant="rect" className="h-64 w-full" />
        </div>
      )}

      {!loading && (
        <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg dark:border dark:border-zinc-700 overflow-hidden">
          {items.length > 0 ? (
            <>
              <SortableTable
                columns={columns}
                data={items}
                rowKey={(row) => String(row.id)}
                rowClassName={(row) =>
                  row.batchId ? 'border-l-2 border-indigo-300 dark:border-indigo-500' : ''
                }
                storageId="curation-review-history"
                defaultSortBy="createdAt"
                defaultSortDir="desc"
                limitHeight={false}
              />
              <InfinityScroll enabled={hasMore && !loading} onLoadMore={loadMore} rootMargin="400px" />
            </>
          ) : (
            <p className="p-8 text-center text-sm text-gray-500 dark:text-zinc-400">
              {hasActiveFilters ? 'No reviews match these filters.' : 'No reviews recorded yet.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
